<script>
  import { Chart, registerables, Tooltip } from "chart.js";
  import { onMount, afterUpdate } from "svelte";

  Chart.register(...registerables);
  if (Tooltip?.positioners) {
    Tooltip.positioners.cursor = function (_, eventPosition) {
      return eventPosition;
    };
  }

  export let width = "100%;";
  export let height = "100%";
  export let dataSets = [];
  export let axes = [];

  export let displayXlabels = true;
  export let displayYlabels = true;
  export let displayLegend = true;
  export let noDecimals = false;
  export let variant = "default";

  let plotCanvas;
  let myChart;
  let initialized = false;

  const colors = [
    "#1c1c20",
    "#4B88A2",
    "#D3D4D9",
    "#D3D4D9",
    "#D3D4D9",
    "#D3D4D9",
    "#D3D4D9",
    "#D3D4D9",
    "#D3D4D9",
    "#D3D4D9",
  ]; // array of colors for the datasets

  const cleanColors = [
    "#1c1c20",
    "#ff5a1f",
    "#2f6f9f",
    "#2f8f5b",
    "#6f5fa8",
  ];

  function isCleanGraph() {
    return variant === "clean";
  }

  function getDatasetStyles(dataset, index) {
    const color = isCleanGraph()
      ? cleanColors[index % cleanColors.length]
      : colors[index];

    return {
      ...dataset,
      borderColor: color,
      backgroundColor: color,
      yAxisID: axes[index],
      tension: isCleanGraph() ? 0.42 : dataset.tension,
      borderWidth: isCleanGraph() ? 3 : dataset.borderWidth,
      pointRadius: isCleanGraph() ? 0 : dataset.pointRadius,
      pointHoverRadius: isCleanGraph() ? 5 : dataset.pointHoverRadius,
      pointHitRadius: isCleanGraph() ? 12 : dataset.pointHitRadius,
      borderCapStyle: isCleanGraph() ? "round" : dataset.borderCapStyle,
      borderJoinStyle: isCleanGraph() ? "round" : dataset.borderJoinStyle,
    };
  }

  const cleanHoverLinePlugin = {
    id: "cleanHoverLine",
    afterDatasetsDraw(chart) {
      if (!isCleanGraph() || !chart.tooltip?._active?.length) return;

      const activePoint = chart.tooltip._active[0];
      const { ctx, chartArea } = chart;
      const x = activePoint.element.x;

      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#1c1c2040";
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    },
  };

  afterUpdate(() => {
    if (initialized) {
      let style = getComputedStyle(document.body);
      let primCol = style.getPropertyValue("--cds-text-01");
      myChart.options.scales.x.ticks.color = primCol;
      myChart.options.scales.y.ticks.color = primCol;
      Chart.defaults.color = primCol;
      primCol = style.getPropertyValue("--cds-text-03");
      myChart.options.scales.x.grid.color = isCleanGraph() ? "#f0f0f0" : primCol;
      myChart.options.scales.y.grid.color = isCleanGraph() ? "#e6e6e6" : primCol;
      myChart.options.scales.x.grid.lineWidth = isCleanGraph() ? 0 : 0.0;
      myChart.options.scales.y.grid.lineWidth = isCleanGraph() ? 1 : 0.0;
      myChart.options.scales.y1.display = displayYlabels && axes.includes("y1");
      myChart.options.plugins.legend.display = displayLegend;
      myChart.options.scales.y.ticks.callback = function (value) {
        if (noDecimals && !Number.isInteger(value)) return null;
        return value;
      };

      myChart.data.datasets = dataSets.map(getDatasetStyles);

      myChart.update();
    }
  });

  function initGraph() {
    var ctx = plotCanvas.getContext("2d");
    myChart = new Chart(ctx, {
      type: "line",
      data: {
        datasets: dataSets.map((dataset, index) => {
          return getDatasetStyles(dataset, index);
        }),
      },
      options: {
        interaction: {
          intersect: false,
          mode: "index",
        },
        layout: {
          padding: isCleanGraph()
            ? { top: 12, right: 18, bottom: 8, left: 8 }
            : 0,
        },
        elements: {
          line: {
            cubicInterpolationMode: isCleanGraph() ? "monotone" : "default",
          },
        },
        font: {
          family: "Inter, sans-serif",
          size: isCleanGraph() ? 12 : 12,
        },
        plugins: {
          tooltip: {
            enabled: true, // Aktiviert das Tooltip-Plugin
            intersect: false, // Zeigt das Tooltip nur an, wenn der Mauszeiger genau über einem Datenpunkt ist
            mode: "index", // Zeigt Tooltip für alle Datenpunkte auf dem vertikalen Index
            backgroundColor: isCleanGraph() ? "#1c1c20" : undefined,
            titleColor: isCleanGraph() ? "#ffffff" : undefined,
            bodyColor: isCleanGraph() ? "#ffffff" : undefined,
            padding: isCleanGraph() ? 12 : undefined,
            cornerRadius: isCleanGraph() ? 6 : undefined,
            displayColors: !isCleanGraph(),
            position: isCleanGraph() ? "cursor" : "average",
          },
          legend: {
            display: displayLegend,
            position: "bottom", // position the legend at the top of the chart
            fullWidth: true, // set the legend to take up the full width of the chart
            labels: {
              boxWidth: 40, // set the width of each legend box
              padding: 20, //padding from legend box
              usePointStyle: isCleanGraph(),
              font: {
                family: "Inter, sans-serif",
              },
            },
          },
        },
        scales: {
          y: {
            display: displayYlabels,
            beginAtZero: true,
            ticks: {
              color: isCleanGraph() ? "#1c1c20" : undefined,
              padding: isCleanGraph() ? 10 : 3,
              font: {
                family: "Inter, sans-serif",
              },
              callback: function (value) {
                if (noDecimals && !Number.isInteger(value)) return null;
                return value;
              },
            },
            grid: {
              color: isCleanGraph() ? "#e6e6e6" : "#f2f2f2", // color of grid
              lineWidth: isCleanGraph() ? 1 : 0.0, // set the line width for y-axis grid
              drawTicks: !isCleanGraph(),
            },
            border: {
              display: !isCleanGraph(),
            },
          },
          y1: {
            position: "right", // align the y-axis scale to the right side
            display: displayYlabels && axes.includes("y1"),
            beginAtZero: true,
            ticks: {
              color: isCleanGraph() ? "#1c1c20" : undefined,
              padding: isCleanGraph() ? 10 : 3,
              font: {
                family: "Inter, sans-serif",
              },
              callback: function (value, index, values) {
                return value + " h";
              },
            },
            grid: {
              color: "#f2f2f2", // color of grid
              lineWidth: 0.0, // set the line width for y-axis grid
              drawTicks: !isCleanGraph(),
            },
            border: {
              display: !isCleanGraph(),
            },
          },
          x: {
            display: displayXlabels,
            beginAtZero: true,
            ticks: {
              color: isCleanGraph() ? "#1c1c20" : undefined,
              padding: isCleanGraph() ? 12 : 3,
              font: {
                family: "Inter, sans-serif",
              },
            },
            grid: {
              color: "#f2f2f2", // color of grid
              lineWidth: 0.0, // set the line width for y-axis grid
              drawTicks: !isCleanGraph(),
            },
            border: {
              display: !isCleanGraph(),
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      plugins: [cleanHoverLinePlugin],
    });

    initialized = true;
  }

  onMount(initGraph);

  export async function download(appName) {
    if (!myChart) return;

    const yScales = Object.keys(myChart.scales).filter((key) =>
      key.startsWith("y"),
    );
    const chartArea = myChart.chartArea;
    const topPadding = 30;
    const width = chartArea.right - chartArea.left + 100;
    const height = chartArea.bottom - chartArea.top + 80 + topPadding;

    let svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
    <style>
      * {background-color: white}
      .axis-label { font-family: Arial, sans-serif; font-size: 12px; fill: #666; }
      .legend-text { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
    </style>`;

    const xScale = myChart.scales.x;

    // process all scales dynamically
    yScales.forEach((scaleKey, index) => {
      const scale = myChart.scales[scaleKey];
      const isRightSide = scaleKey === "y1";

      scale.ticks.forEach((tick, i) => {
        const y = scale.getPixelForTick(i) - chartArea.top + topPadding;

        if (index === 0) {
          svg += `<line x1="50" y1="${y}" x2="${width - 50}" y2="${y}" stroke="${i === 0 ? "#1c1c2020" : "#77778820"}" stroke-width="1"/>`;
        }

        const labelX = isRightSide ? width - 45 : 45;
        const anchor = isRightSide ? "start" : "end";
        const suffix = scaleKey === "y1" ? " h" : "";

        svg += `<text x="${labelX}" y="${y + 4}" text-anchor="${anchor}" class="axis-label">${scale.getLabelForValue(tick.value)}${suffix}</text>`;
      });
    });

    // Grid X
    if (xScale) {
      xScale.ticks.forEach((tick, i) => {
        const x = xScale.getPixelForTick(i) - chartArea.left + 50;
        svg += `<line x1="${x}" y1="${topPadding}" x2="${x}" y2="${height - 80}"  stroke="${i === 0 || i === xScale.ticks.length - 1 ? "#1c1c2020" : "#77778820"}" stroke-width="1"/>`;
        svg += `<text x="${x}" y="${height - 45}" text-anchor="middle" class="axis-label">${xScale.getLabelForValue(tick.value)}</text>`;
      });
    }

    // data line - visible only
    const visibleDatasets = myChart.data.datasets
      .reverse()
      .filter((_, i) => !myChart.getDatasetMeta(i).hidden);

    myChart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = myChart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return; // Skip hidden datasets

      const yAxisID = dataset.yAxisID || "y";
      const scale = myChart.scales[yAxisID];

      if (!scale) return;

      let pathData = "";

      dataset.data.forEach((point, i) => {
        const x = xScale.getPixelForValue(point.x) - chartArea.left + 50;
        const y = scale.getPixelForValue(point.y) - chartArea.top + topPadding;
        pathData += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
      });

      svg += `<path d="${pathData}" stroke="${dataset.backgroundColor}" stroke-width="3" fill="none"/>`;

      // Points
      dataset.data.forEach((point, i) => {
        const x = xScale.getPixelForValue(point.x) - chartArea.left + 50;
        const y = scale.getPixelForValue(point.y) - chartArea.top + topPadding;
        svg += `<circle cx="${x}" cy="${y}" r="3.5" fill="${dataset.backgroundColor}"/>`;
      });
    });

    // Legend - visible datasets only
    let totalLegendWidth = 0;
    visibleDatasets.forEach((dataset) => {
      totalLegendWidth += (dataset.label?.length || 10) * 8 + 70; // 40 rect + 30 spacing
    });

    totalLegendWidth -= 30;

    let legendY = height - 25;
    let legendX = (width - totalLegendWidth) / 2;
    myChart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = myChart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return; // Skip hidden datasets

      svg += `<rect x="${legendX}" y="${legendY - 8}" width="40" height="15" fill="${dataset.backgroundColor}"/>`;
      svg += `<text x="${legendX + 50}" y="${legendY}" class="legend-text">${dataset.label || `Serie ${datasetIndex + 1}`}</text>`;
      legendX += (dataset.label?.length || 10) * 8 + 70;
    });

    svg += "</svg>";

    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    appName = appName || "Aplications";

    const a = document.createElement("a");
    a.href = url;
    a.download = `${appName}-workload.svg`;
    a.click();

    URL.revokeObjectURL(url);
  }

  var fullScreenActive = false;

  function fullscreen() {
    myChart.destroy();
    fullScreenActive = !fullScreenActive;
    fullScreenDiv.classList.toggle("fullscreen");
    plotCanvas.height = fullScreenActive
      ? getComputedStyle(plotDiv).height - 60
      : height - 60;
    initGraph();
  }

  let fullScreenDiv;
  let plotDiv;
</script>

<div bind:this={fullScreenDiv} style="height:100%; width:{width}">
  <div style="width:{width}; height:{height};" bind:this={plotDiv}>
    <canvas bind:this={plotCanvas} />
  </div>
</div>

<style>
  canvas {
    height: 100%;
    width: 100%;
  }
</style>
